import { Button, Column, DateField, TextField, TimeField, WarningBanner, isSuccess, useToasts } from "@dgs/core";
import { addWeeks, isAfter, parseISO } from "date-fns";
import { useField, useFormikContext } from "formik";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AirportSingleSelectField } from "../../../../../../../shared/AirportSingleSelectField";
import { FlightRouteResource, flightService } from "../../../../../shared/api/registrationForms/flightService";
import { TravelSegmentFlightFormValues } from "../../../../../shared/types/travelSegment";

export const emptyFlightFormValues = (): TravelSegmentFlightFormValues => ({
	id: Symbol("flight"),
	type: "flight",
	arrivalLocation: null,
	departureDate: "",
	departureTime: "",
	arrivalDate: "",
	arrivalTime: "",
	departureLocation: null,
	serviceClass: "",
	identifier: "",
	additionalIdentifiers: [],
});

export const TravelSegmentFlightFormFields: FC = () => {
	const { t } = useTranslation();
	const [{ value: flightNumberValue }] = useField("identifier");
	const [{ value: departureDateValue }] = useField("departureDate");
	const { setValues } = useFormikContext();
	const { showToast } = useToasts();
	const [isAutoFilled, setIsAutoFilled] = useState(false);

	const getFlightInfo = useCallback(async () => {
		const request = { departureDate: departureDateValue, flightNumber: flightNumberValue };
		const response = await flightService.getFlightRoute(request);
		if (isSuccess(response)) {
			void setValues(flightRouteToFormValues(response.data.data));
			if (isAfter(parseISO(request.departureDate), addWeeks(new Date(), 1))) {
				setIsAutoFilled(true);
			}
		} else {
			showToast({ type: "error", title: t("Error"), body: t("A route for the flight could not be found.") });
		}
	}, [departureDateValue, flightNumberValue, setValues, showToast, t]);

	return (
		<>
			{isAutoFilled && (
				<WarningBanner
					warning={t(
						"Your flight information has been filled out automatically. Please verify that the details are correct.",
					)}
				/>
			)}
			<TextField name="identifier" label={t("Flight number")} />
			<DateField name="departureDate" label={t("Departure date")} required />
			<Column $alignItems="end">
				<Button
					size="small"
					title={t("Provide flight number and departure date to obtain flight information.")}
					onClick={getFlightInfo}
					type="button"
					disabled={!flightNumberValue || !departureDateValue}
				>
					{t("Autofill flight information")}
				</Button>
			</Column>
			<TimeField name="departureTime" label={t("Departure time")} required />
			<AirportSingleSelectField name="departureLocation" label={t("Departure airport")} required />
			<AirportSingleSelectField name="arrivalLocation" label={t("Arrival airport")} required />
			<DateField name="arrivalDate" label={t("Arrival date")} required />
			<TimeField name="arrivalTime" label={t("Arrival time")} required />
			<TextField name="serviceClass" label={t("Service class")} />
		</>
	);
};

const flightRouteToFormValues = (flightRoute: FlightRouteResource): TravelSegmentFlightFormValues => ({
	...emptyFlightFormValues(),
	arrivalDate: flightRoute.arrivalDate,
	arrivalLocation: flightRoute.arrivalLocation,
	arrivalTime: flightRoute.arrivalTime,
	departureDate: flightRoute.departureDate,
	departureLocation: flightRoute.departureLocation,
	departureTime: flightRoute.departureTime,
	identifier: flightRoute.flightNumber,
});
